// Reglas de las reservas con proveedores (restaurantes, caballerizas y experiencias). Sin base de datos: las usan el servidor,
// la vista previa del navegador y las pruebas. Dinero en centavos.
// Restaurantes: solo reservan mesa, no hay pago. Caballerizas: el cobro se hace al ACEPTAR, a la cuenta del dueño.

// «experiencia»: razers, cuatrimotos, tirolesa… funcionan como la caballeriza (horarios con cupo y cobro por persona); su categoría dice qué es.
export const TIPOS_PROVEEDOR = ["restaurante", "caballeriza", "experiencia"] as const;
export type TipoProveedor = (typeof TIPOS_PROVEEDOR)[number];
export const NOMBRE_TIPO: Record<TipoProveedor, string> = { restaurante: "Restaurante", caballeriza: "Caballeriza", experiencia: "Experiencia" };
export const CATEGORIAS_EXPERIENCIA = ["Razer", "Cuatrimoto", "Tirolesa", "Gotcha", "Ciclismo de montaña", "Senderismo guiado", "Pesca", "Otra"] as const;
/** Lo que se le muestra a la persona: la categoría de la experiencia o el tipo. */
export const nombreDe = (tipo: string, categoria?: string | null) => (tipo === "experiencia" && categoria ? categoria : esTipo(tipo) ? NOMBRE_TIPO[tipo] : tipo === "cabanas" ? "Cabañas" : tipo);
/** Caballerizas y experiencias cobran por persona al aceptar; los restaurantes solo apartan mesa. */
export const cobraPorPersona = (tipo: string) => tipo === "caballeriza" || tipo === "experiencia";

export const ESTADOS_SOLICITUD = ["solicitada", "aceptada", "rechazada", "cancelada", "llegó", "no llegó"] as const;
export type EstadoSolicitud = (typeof ESTADOS_SOLICITUD)[number];

export const MAX_PERSONAS = 40;
export const DIAS_PARA_RESERVAR = 90;

const rango = (desde: number, hasta: number) => Array.from({ length: hasta - desde + 1 }, (_, i) => `${String(desde + i).padStart(2, "0")}:00`);
/** Horas en que cada tipo puede ofrecer lugares. Cada hora es un bloque independiente. */
const HORAS: Record<TipoProveedor, string[]> = { restaurante: rango(8, 20), caballeriza: rango(8, 17), experiencia: rango(8, 18) };

export const esTipo = (t: unknown): t is TipoProveedor => (TIPOS_PROVEEDOR as readonly unknown[]).includes(t);
export const horasDe = (tipo: string): string[] => (esTipo(tipo) ? HORAS[tipo] : []);

// ───────── Fechas (zona de México; el servidor y el navegador deben coincidir)
const ZONA = "America/Mexico_City";
export const hoyMexico = (ahora: Date = new Date()) => new Intl.DateTimeFormat("en-CA", { timeZone: ZONA, year: "numeric", month: "2-digit", day: "2-digit" }).format(ahora);
export const horaMexico = (ahora: Date | string) => new Intl.DateTimeFormat("es-MX", { timeZone: ZONA, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(ahora));
export const sumarDias = (iso: string, dias: number) => { const d = new Date(`${iso}T00:00:00.000Z`); d.setUTCDate(d.getUTCDate() + dias); return d.toISOString().slice(0, 10); };
const fechaValida = (f: string) => /^\d{4}-\d{2}-\d{2}$/.test(f) && new Date(`${f}T00:00:00.000Z`).toISOString().slice(0, 10) === f;

// ───────── Cobro
/** Caballerizas y experiencias cobran. La solicitud lleva el monto cotizado; el cargo se hace al aceptar. */
export const metodoPagoDe = (tipo: string) => (cobraPorPersona(tipo) ? "cargo" : "ninguno");
export const montoDe = (tipo: string, personas: number, precioPorLugarCentavos: number) => (cobraPorPersona(tipo) ? personas * precioPorLugarCentavos : 0);
/** Hay que cobrar al aceptar: solo con cargo, con monto y todavía sin cobrar. */
export const debeCobrarAlAceptar = (s: { metodoPago: string; montoCentavos: number; cobradoEn?: unknown }) => s.metodoPago === "cargo" && s.montoCentavos > 0 && !s.cobradoEn;

// ───────── Disponibilidad
export const lugaresLibres = (d: { lugares: number; usados: number }) => Math.max(0, d.lugares - d.usados);
export const cabeReserva = (d: { lugares: number; usados: number }, personas: number) => personas >= 1 && lugaresLibres(d) >= personas;
/** Un horario no puede ofrecer más que el cupo del proveedor ni menos de lo ya reservado. */
export function ajusteValido(lugares: number, usados: number, cupoMaximo: number): string | null {
  if (!Number.isInteger(lugares) || lugares < 0) return "Los lugares deben ser un número entero (0 o más)";
  if (lugares > cupoMaximo) return `Ofreces como máximo ${cupoMaximo} por horario`;
  if (lugares < usados) return `Ya hay ${usados} reservados en ese horario; no puedes ofrecer menos`;
  return null;
}

// ───────── Estados de la solicitud
export type AccionSolicitud = "aceptar" | "rechazar" | "cancelar" | "llego" | "noLlego";
const TRANSICIONES: Record<AccionSolicitud, { desde: EstadoSolicitud[]; a: EstadoSolicitud; error: string }> = {
  aceptar: { desde: ["solicitada"], a: "aceptada", error: "Solo se acepta una solicitud por responder" },
  rechazar: { desde: ["solicitada"], a: "rechazada", error: "Solo se rechaza una solicitud por responder" },
  cancelar: { desde: ["solicitada", "aceptada"], a: "cancelada", error: "Esta reserva ya no se puede cancelar" },
  llego: { desde: ["aceptada", "no llegó"], a: "llegó", error: "Esta reserva ya tiene su llegada registrada" },
  noLlego: { desde: ["aceptada"], a: "no llegó", error: "Solo se marca «no llegó» en una reserva aceptada" },
};
export function transicion(estado: string, accion: AccionSolicitud): EstadoSolicitud {
  const t = TRANSICIONES[accion];
  if (!t) throw new Error("Acción no válida");
  if (!t.desde.includes(estado as EstadoSolicitud)) throw new Error(t.error);
  return t.a;
}
/** Al rechazar o cancelar, los lugares vuelven a la disponibilidad. */
export const liberaLugares = (estado: string) => estado === "rechazada" || estado === "cancelada";

// ───────── Solicitud del cliente
export type DatosSolicitud = { proveedorId: string; fecha: string; hora: string; personas: number; clienteNombre: string; clienteTelefono: string; clienteCorreo?: string };

export const PASEOS: Record<string, string> = { "1h": "Paseo de 1 hora", "2h": "Paseo de 2 horas", cascada: "Caballo a la cascada" };
export const EXPERIENCIAS = ["principiante", "intermedio", "experimentado"] as const;
export const TIPOS_EVENTO = ["Celebración", "Grupo grande", "Otro"] as const;

/** Detalles que pide cada tipo de lugar, además de la fecha, la hora y las personas. */
export type DetallesSolicitud = {
  tipoPaseo?: string; nivelExperiencia?: string; contactoEmergencia?: string; telefonoEmergencia?: string;
  aceptaSeguridad?: boolean; pedidoEspecial?: boolean; tipoEvento?: string; notas?: string;
};

/** Primer error de los detalles, en español, o null. Caballerizas piden paseo, experiencia, seguridad y contacto de emergencia. */
export function validarDetalles(tipo: string, d: DetallesSolicitud): string | null {
  if (tipo === "caballeriza") {
    if (!d.tipoPaseo || !(d.tipoPaseo in PASEOS)) return "Elige el tipo de paseo";
    if (!(EXPERIENCIAS as readonly string[]).includes(String(d.nivelExperiencia))) return "Dinos el nivel de experiencia de los jinetes";
    if (String(d.contactoEmergencia ?? "").trim().length < 3) return "Escribe un contacto de emergencia";
    if (!/^\d{10}$/.test(String(d.telefonoEmergencia ?? "").replace(/[\s()-]/g, ""))) return "El teléfono de emergencia debe tener 10 dígitos";
    if (d.aceptaSeguridad !== true) return "Acepta el reglamento de seguridad para reservar el paseo";
  }
  if (tipo === "restaurante" && d.pedidoEspecial && !(TIPOS_EVENTO as readonly string[]).includes(String(d.tipoEvento))) return "Elige el tipo de evento";
  return null;
}

/** Los detalles tal como se guardan: vacíos si no aplican al tipo de lugar. */
export function detallesDe(d: DetallesSolicitud) {
  return {
    tipoPaseo: d.tipoPaseo ?? null,
    nivelExperiencia: d.nivelExperiencia ?? null,
    contactoEmergencia: d.contactoEmergencia?.trim() || null,
    telefonoEmergencia: d.telefonoEmergencia?.replace(/[\s()-]/g, "") || null,
    aceptaSeguridad: d.aceptaSeguridad === true,
    pedidoEspecial: d.pedidoEspecial === true,
    tipoEvento: d.pedidoEspecial ? d.tipoEvento ?? null : null,
    notas: d.notas?.trim().slice(0, 500) || null,
  };
}

/** Primer error en español o null. "hoy" es la fecha de México (AAAA-MM-DD). */
export function validarSolicitud(d: Partial<DatosSolicitud>, tipo: string, hoy: string): { error: string | null; datos: DatosSolicitud } {
  const datos: DatosSolicitud = {
    proveedorId: String(d.proveedorId ?? ""), fecha: String(d.fecha ?? ""), hora: String(d.hora ?? ""), personas: Number(d.personas),
    clienteNombre: String(d.clienteNombre ?? "").trim(), clienteTelefono: String(d.clienteTelefono ?? "").replace(/[\s()-]/g, ""),
    clienteCorreo: String(d.clienteCorreo ?? "").trim().toLowerCase() || undefined,
  };
  const max = sumarDias(hoy, DIAS_PARA_RESERVAR);
  const error =
    (!datos.proveedorId ? "Elige un lugar" : null) ??
    (!fechaValida(datos.fecha) ? "Elige una fecha" : null) ??
    (datos.fecha < hoy ? "Esa fecha ya pasó" : null) ??
    (datos.fecha > max ? `Solo se reserva con ${DIAS_PARA_RESERVAR} días de anticipación` : null) ??
    (!horasDe(tipo).includes(datos.hora) ? "Elige una hora de la lista" : null) ??
    (!Number.isInteger(datos.personas) || datos.personas < 1 || datos.personas > MAX_PERSONAS ? `Son de 1 a ${MAX_PERSONAS} personas` : null) ??
    (datos.clienteNombre.length < 3 ? "Escribe tu nombre" : null) ??
    (!/^\d{10}$/.test(datos.clienteTelefono) ? "El WhatsApp debe tener 10 dígitos" : null) ??
    (datos.clienteCorreo && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(datos.clienteCorreo) ? "El correo no es válido" : null);
  return { error, datos };
}

// ───────── Validación del QR al llegar
export type EstadoQr = "valida" | "no_valida" | "ya_usada" | "cancelada" | "de_otro_lugar";
export type ResultadoQr = { valida: boolean; estado: EstadoQr; titulo: string; detalle: string; personas: number; hora: string; nombre: string };
export type SolicitudQr = { proveedorId: string; estado: string; fecha: string; hora: string; personas: number; clienteNombre: string; llegadaEn?: string | Date | null };

const primerNombre = (n: string) => String(n ?? "").trim().split(/\s+/)[0] ?? "";

/** Decide si un QR deja pasar. El orden importa: primero el lugar, luego el estado, después el día y por último el uso. */
export function evaluarQr(s: SolicitudQr | null | undefined, ctx: { proveedorId?: string | null; hoy: string }): ResultadoQr {
  if (!s) return { valida: false, estado: "no_valida", titulo: "Este QR no es de Mazamitla Sierra Tours", detalle: "Revisa que sea el QR de la reserva.", personas: 0, hora: "", nombre: "" };
  const base = { personas: s.personas, hora: s.hora, nombre: primerNombre(s.clienteNombre) };
  const no = (estado: EstadoQr, titulo: string, detalle: string): ResultadoQr => ({ valida: false, estado, titulo, detalle, ...base });
  if (ctx.proveedorId && ctx.proveedorId !== s.proveedorId) return no("de_otro_lugar", "Esta reserva es de otro lugar", "Pídele a la persona que vaya al lugar de su reserva.");
  if (s.estado === "cancelada") return no("cancelada", "Reserva cancelada", "No se atiende esta reserva.");
  if (s.estado === "solicitada" || s.estado === "rechazada") return no("no_valida", "Esta reserva no está confirmada", "Revisa el panel: no se aceptó.");
  if (s.fecha !== ctx.hoy) return no("no_valida", "Es de otro día", `Esta reserva es para el ${s.fecha}.`);
  if (s.estado === "llegó") return no("ya_usada", "Este QR ya se usó", s.llegadaEn ? `Ya entró a las ${horaMexico(s.llegadaEn)}.` : "Ya entró.");
  return { valida: true, estado: "valida", titulo: "Reserva válida", detalle: `${s.personas} ${s.personas === 1 ? "persona" : "personas"} · ${s.hora}`, ...base };
}

// ───────── Fotos del lugar (las sube el propio proveedor)
export const MAX_FOTOS_PROVEEDOR = 8;
/** Tamaño máximo de la foto ya codificada (data URL). El navegador la reduce a 1200 px antes de subirla. */
export const FOTO_MAX_CARACTERES = 900_000;

/** Primer error al subir una foto, en español, o null. "cuantas" son las fotos que ya tiene el lugar. */
export function validarFoto(archivo: unknown, alt: unknown, cuantas: number): string | null {
  if (cuantas >= MAX_FOTOS_PROVEEDOR) return `Puedes tener hasta ${MAX_FOTOS_PROVEEDOR} fotos. Quita una para subir otra`;
  const a = String(archivo ?? "");
  if (!/^data:image\/(webp|jpeg|png);base64,/.test(a)) return "La foto debe ser WebP, JPG o PNG";
  if (a.length > FOTO_MAX_CARACTERES) return "La foto es muy pesada. Usa una más chica";
  if (String(alt ?? "").trim().length < 3) return "Describe brevemente qué se ve en la foto";
  return null;
}

// ───────── Reprogramación por lluvia (caballerizas)
const DIAS_MAX = DIAS_PARA_RESERVAR;
/** La nueva fecha debe ser de hoy en adelante, distinta de la original y dentro de la ventana de reservas. */
export function validarReprogramacion(origen: string, nueva: string, hoy: string): string | null {
  if (!fechaValida(nueva)) return "Elige la nueva fecha";
  if (nueva === origen) return "La nueva fecha debe ser distinta a la original";
  if (nueva < hoy) return "La nueva fecha ya pasó";
  const max = new Date(Date.parse(`${hoy}T00:00:00Z`) + DIAS_MAX * 86400000).toISOString().slice(0, 10);
  if (nueva > max) return `Solo se reprograma con ${DIAS_MAX} días de anticipación`;
  return null;
}
